<script lang="ts">
	/**
	 * Options a buyer **may** take at purchase (Design §4.8).
	 *
	 * `ClickwrapsPanel`'s sibling, authored beside it and scoped the same way, and the difference
	 * is one word that runs through the whole screen: a term is required and gates the purchase, an
	 * option is offered and gates nothing. A customer who ticks none of these buys at list price.
	 *
	 * What an option can do that a term cannot is two things, and a store may use either or both:
	 *
	 * **It can discount the purchase** — a percentage, or a fixed amount per currency. This is the
	 * half that is money, and it is why editing the discount bumps the version exactly as editing
	 * the wording does: a buyer quoted "save 15%" on a page already open must not be charged
	 * against 10%.
	 *
	 * **It can turn a feature on.** The key is shipped in the entitlement payload, so the
	 * application reads `options` and draws the splash logo the customer took the discount for
	 * agreeing to. That key is **frozen once anybody has taken the option**: it is copied onto
	 * every selection and every payload, so changing it would strand existing holders on the old
	 * string while new buyers got the new one, with nothing failing anywhere.
	 *
	 * And, as with a term, **an option is retired rather than deleted** once somebody has taken it:
	 * the selection is the record of a discount the store actually gave.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Badge, Button, Checkbox, Input, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell, Textarea, Toggle } from 'flowbite-svelte';
	import { ConfirmButton, EmptyState, FormField, FormModal, Panel, SelectField } from '../../../components';
	import { createProductOption, deleteProductOption, updateProductOption } from '../../../AppNetwork';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import { formatMoney, majorUnitsFrom, minorUnitsFrom } from '../../../Utilities';
	import type {
		AdminProductOption,
		Product,
		ProductFamily,
		StorefrontCurrency
	} from '../../../AppTypes';

	export let options: AdminProductOption[] = [];
	export let products: Product[] = [];
	export let families: ProductFamily[] = [];
	export let currencies: StorefrontCurrency[] = [];
	export let loading: boolean = false;

	const dispatcher = createEventDispatcher();

	let isAdding: boolean = false;
	let saving: boolean = false;
	let editingUUID: string = '';

	/**
	 * What an option does to the price, as **two** controls rather than one.
	 *
	 * The direction is asked separately from the kind because the stored value is *signed* —
	 * negative takes money off, positive puts it on — and asking an admin to express that by typing
	 * a minus sign is the mistake nobody notices: a discount authored as a surcharge charges every
	 * buyer twice the intended amount in the wrong direction, and the row looks fine. The form
	 * takes plain positive numbers and applies the sign from this.
	 *
	 * "No price change" is a first-class choice rather than an empty state: an option that exists
	 * purely to turn a feature on is entirely ordinary, and a merely-blank amount cannot be told
	 * from one somebody forgot to fill in.
	 */
	const EFFECTS = [
		{ value: 'none', name: 'No price change — a feature only' },
		{ value: 'discount', name: 'Discount — takes money off' },
		{ value: 'charge', name: 'Extra — adds to the price' }
	];

	const KINDS = [
		{ value: 'percent', name: 'Percentage of the plan' },
		{ value: 'fixed', name: 'Fixed amount' }
	];

	/** Whether the money moves every period or only once — a standing extra against a setup fee. */
	const CADENCES = [
		{ value: 'recurring', name: 'Every period' },
		{ value: 'once', name: 'Once, on the first invoice' }
	];

	function emptyDraft() {
		return {
			productUUIDs: [] as string[],
			familySlugs: [] as string[],
			key: '',
			title: '',
			bodyHTML: '',
			displayOrder: 0,
			effect: 'none',
			kind: 'percent',
			// Always the plain magnitude an admin types; the sign comes from `effect`.
			percent: 10,
			cadence: 'recurring',
			// Keyed by currency and holding **the decimal an admin types** — "5.00", not 500.
			// Both directions are string arithmetic through `minorUnitsFrom` / `majorUnitsFrom`,
			// because `4.99 * 100` is `498.99999999999994` and a cent short on every amount
			// ending in .99 (recipe 8d).
			amounts: {} as Record<string, string>
		};
	}

	let draft = emptyDraft();
	let edit = {
		key: '',
		title: '',
		bodyHTML: '',
		displayOrder: 0,
		isActive: true,
		effect: 'none',
		kind: 'percent',
		percent: 10,
		cadence: 'recurring',
		amounts: {} as Record<string, string>,
		productUUIDs: [] as string[],
		familySlugs: [] as string[]
	};

	function fnRowFor(code: string): StorefrontCurrency | undefined {
		return currencies.find(currency => currency.code === code);
	}

	/**
	 * The decimal places a currency uses.
	 *
	 * Zero where the row is missing rather than two: guessing two is the zero-decimal bug, and it
	 * is wrong silently because the figure still looks like money.
	 */
	function fnDigitsOf(code: string): number {
		return fnRowFor(code)?.minorUnitDigits ?? 0;
	}

	function fnFormat(code: string, amountMinor: number): string {
		const row = fnRowFor(code);
		return row === undefined ? `${amountMinor} (minor units, ${code.toUpperCase()})` : formatMoney(amountMinor, row);
	}

	/** Toggling one target, kept out of the markup so the lists stay readable. */
	function fnToggle(list: string[], value: string, event: Event): string[] {
		return (event.target as HTMLInputElement).checked
			? [...list, value]
			: list.filter(entry => entry !== value);
	}

	/** `none` is a real answer and travels as null; the server clears the percentage with it. */
	function fnAdjustmentTypeOf(form: { effect: string; kind: string }): 'percent' | 'fixed' | null {
		if (form.effect === 'none') return null;
		return form.kind === 'fixed' ? 'fixed' : 'percent';
	}

	/**
	 * The sign the stored value carries: **−1 takes money off, +1 puts it on**.
	 *
	 * Applied here, once, on the way out — so every field on this form is a plain positive number
	 * and nothing depends on an admin remembering a convention.
	 */
	function fnSignOf(form: { effect: string }): number {
		return form.effect === 'charge' ? 1 : -1;
	}

	/**
	 * The typed decimals as minor units, dropping anything that is not an amount.
	 *
	 * An unparseable figure is **omitted rather than sent as zero** (recipe 8d): a typo read as
	 * zero is a discount of nothing that looks configured, and nothing about the row would say so.
	 * A blank currency is left out for the same reason it is on a price — that is how an option is
	 * made unavailable in it, which the storefront then flags rather than hides.
	 */
	function fnAmountsOf(amounts: Record<string, string>): Record<string, number> {
		const parsed: Record<string, number> = {};
		for (const [code, typed] of Object.entries(amounts)) {
			const minor = minorUnitsFrom(typed, fnDigitsOf(code));
			if (minor !== null) {
				parsed[code] = minor;
			}
		}
		return parsed;
	}

	$: editing = options.find(option => option.uuid === editingUUID) ?? null;

	/** A `fixed` option with no amount anywhere is refused by the server, so neither form saves one. */
	function fnComplete(form: {
		key: string;
		title: string;
		bodyHTML: string;
		effect: string;
		kind: string;
		amounts: Record<string, string>;
	}): boolean {
		if (form.key.trim().length === 0 || form.title.trim().length === 0 || form.bodyHTML.trim().length === 0) {
			return false;
		}
		return (
			fnAdjustmentTypeOf(form) !== 'fixed' || Object.keys(fnAmountsOf(form.amounts)).length > 0
		);
	}

	$: canCreate =
		!saving &&
		fnComplete(draft) &&
		draft.productUUIDs.length + draft.familySlugs.length > 0;
	$: canSaveEdit =
		!saving &&
		fnComplete(edit) &&
		edit.productUUIDs.length + edit.familySlugs.length > 0;

	/**
	 * Whether Save would bump the version, so the consequence is visible before it happens.
	 *
	 * Two edits do it, and they are one rule twice: a rendered page cannot honour either. New
	 * wording is a tick against words nobody read; a new discount is a figure quoted on the
	 * storefront that the payment page will not match — which is the same fault plus money.
	 */
	$: wordingChanged = editing !== null && edit.bodyHTML !== editing.bodyHTML;
	/**
	 * The **direction** is in this too, and it is the half that matters most: an option flipped
	 * from taking €5 off to putting €5 on is a ten-euro swing on a page already open.
	 */
	$: priceChanged =
		editing !== null &&
		(fnAdjustmentTypeOf(edit) !== editing.adjustmentType ||
			(fnAdjustmentTypeOf(edit) === 'percent' &&
				fnSignOf(edit) * Number(edit.percent) !== editing.percentAdjustment) ||
			(edit.cadence === 'once') !== editing.chargeOnce ||
			(fnAdjustmentTypeOf(edit) === 'fixed' &&
				JSON.stringify(fnSignedAmounts(edit)) !== JSON.stringify(editing.amounts)));
	$: versionBumps = wordingChanged || priceChanged;

	/** What the amounts fields come to once the direction is applied. */
	function fnSignedAmounts(form: { effect: string; kind: string; amounts: Record<string, string> }): Record<string, number> {
		if (fnAdjustmentTypeOf(form) !== 'fixed') return {};
		const sign = fnSignOf(form);
		return Object.fromEntries(
			Object.entries(fnAmountsOf(form.amounts)).map(([code, value]) => [code, sign * value])
		);
	}

	/** What one option takes off, for the list. Rendered per currency, never as one number. */
	function fnSavingOf(option: AdminProductOption): string {
		if (option.adjustmentType === 'percent' && option.percentAdjustment !== null) {
			// The sign spelled out, because a bare "−15%" in a table column is read as a typo about
			// as often as it is read as a discount.
			return `${option.percentAdjustment < 0 ? '−' : '+'}${Math.abs(option.percentAdjustment)}%`;
		}
		if (option.adjustmentType === 'fixed') {
			const entries = Object.entries(option.amounts);
			// Every currency, because minor units in two currencies are different units and one
			// figure standing for all of them is a number that is wrong somewhere.
			return entries.length === 0
				? 'No amounts'
				: entries
					.map(([code, minor]) => `${minor < 0 ? '−' : '+'}${fnFormat(code, Math.abs(minor))}`)
					.join(' · ');
		}
		return '—';
	}

	function fnStartEdit(option: AdminProductOption): void {
		editingUUID = option.uuid;
		edit = {
			key: option.key,
			title: option.title,
			bodyHTML: option.bodyHTML,
			displayOrder: option.displayOrder,
			isActive: option.isActive,
			effect:
				option.adjustmentType === null
					? 'none'
					: (option.percentAdjustment ?? Object.values(option.amounts)[0] ?? -1) < 0
						? 'discount'
						: 'charge',
			kind: option.adjustmentType ?? 'percent',
			// Back to the plain magnitude the form takes; the direction is the control above.
			percent: Math.abs(option.percentAdjustment ?? 10),
			cadence: option.chargeOnce ? 'once' : 'recurring',
			// Back to the decimals an admin reads, per currency, and **every** currency the option
			// holds — rebuilding from a single base would silently drop the others.
			amounts: Object.fromEntries(
				Object.entries(option.amounts).map(([code, minor]) => [
					code,
					majorUnitsFrom(Math.abs(minor), fnDigitsOf(code))
				])
			) as Record<string, string>,
			productUUIDs: [...option.productUUIDs],
			familySlugs: [...option.familySlugs]
		};
	}

	async function fnCreate(): Promise<void> {
		saving = true;
		try {
			await createProductOption({
				// Both lists, always: the server refuses an option offered to nobody, and sending
				// them together is what lets one offer cover a line and a plan at once.
				productUUIDs: draft.productUUIDs,
				familySlugs: draft.familySlugs,
				key: draft.key.trim(),
				title: draft.title.trim(),
				bodyHTML: draft.bodyHTML.trim(),
				displayOrder: Number(draft.displayOrder),
				adjustmentType: fnAdjustmentTypeOf(draft),
				percentAdjustment:
					fnAdjustmentTypeOf(draft) === 'percent' ? fnSignOf(draft) * Number(draft.percent) : null,
				amounts: fnSignedAmounts(draft),
				chargeOnce: draft.cadence === 'once'
			});
			toastSuccess(`${draft.title.trim()} added.`);
			draft = emptyDraft();
			isAdding = false;
			dispatcher('changed');
		} catch (error) {
			toastError('The option was not added.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	/**
	 * Deletes an option, which always takes it off every product and family it named.
	 *
	 * The row itself only goes when nobody took it. A selection records a discount the store
	 * actually gave and it names the option, so one that has been taken is retired instead — and
	 * the toast says which happened, because an option still in the list after Delete otherwise
	 * reads as a failure.
	 */
	async function fnDelete(option: AdminProductOption): Promise<void> {
		saving = true;
		try {
			const result = await deleteProductOption(option.uuid);
			if (result.deleted) {
				toastSuccess(`${option.title} deleted.`);
			} else {
				toastSuccess(
					`${option.title} retired.`,
					`${result.selectionCount} purchase${result.selectionCount === 1 ? '' : 's'} took it, so the record is kept. It is offered on nothing and nobody new is shown it.`
				);
			}
			if (editingUUID === option.uuid) {
				editingUUID = '';
			}
			dispatcher('changed');
		} catch (error) {
			toastError('The option was not deleted.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnSaveEdit(): Promise<void> {
		if (editing === null) {
			return;
		}
		saving = true;
		try {
			await updateProductOption(editing.uuid, {
				// Sent only while it is still editable, so a form that renders it read-only cannot
				// send back a value the server would refuse.
				...(editing.keyIsEditable ? { key: edit.key.trim() } : {}),
				title: edit.title.trim(),
				bodyHTML: edit.bodyHTML.trim(),
				displayOrder: Number(edit.displayOrder),
				isActive: edit.isActive,
				// Always sent, so switching the discount off actually stops it discounting.
				adjustmentType: fnAdjustmentTypeOf(edit),
				percentAdjustment:
					fnAdjustmentTypeOf(edit) === 'percent' ? fnSignOf(edit) * Number(edit.percent) : null,
				amounts: fnSignedAmounts(edit),
				chargeOnce: edit.cadence === 'once',
				// Sent together, because either list alone **replaces** the scope on the server.
				productUUIDs: edit.productUUIDs,
				familySlugs: edit.familySlugs
			});
			toastSuccess(`${edit.title.trim()} saved.`);
			editingUUID = '';
			dispatcher('changed');
		} catch (error) {
			toastError('The option was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}
</script>

<Panel title="Product options" faIcon="sliders">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={() => (isAdding = true)}>
			<i class="fa-solid fa-plus mr-2" />Add an option
		</Button>
	</svelte:fragment>

	<p class="text-sm text-default mb-4">
		<i class="fa-solid fa-circle-info mr-2" />Checkboxes the buyer <strong>may</strong> tick, in
		exchange for a discount, a feature, or both. Nothing here blocks a purchase. The key is what
		the application reads back, so it can enable what the customer agreed to — and a discounting
		option cannot be combined with a discount code, because the payment provider takes one
		discount per checkout.
	</p>

	{#if loading}
		<EmptyState variant="loading" title="Loading options…" />
	{:else if options.length === 0}
		<EmptyState
			title="No product options."
			description="Nothing extra is offered at checkout. Add an option to offer one: a discount for showing your logo, a feature the customer opts into, or a term they take on by choice."
		>
			<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add the first option</Button>
		</EmptyState>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Option</TableHeadCell>
				<TableHeadCell>Offered on</TableHeadCell>
				<TableHeadCell>Saves</TableHeadCell>
				<TableHeadCell>Version</TableHeadCell>
				<TableHeadCell>Taken</TableHeadCell>
				<TableHeadCell>State</TableHeadCell>
				<TableHeadCell />
			</TableHead>
			<TableBody>
				{#each options as option (option.uuid)}
					<TableBodyRow>
						<TableBodyCell class="dataTable font-medium">
							{option.title}
							<!--
								The key on the list, not only in the form: it is what an integration
								switches on, and "the feature is not turning on" is answered by
								reading it here against what the application expects.
							-->
							<span class="block text-xs font-normal font-mono text-default">{option.key}</span>
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<!--
								Every target listed rather than a count, as the terms panel has it:
								the question an admin asks here is which line an offer covers.
							-->
							<div class="flex flex-wrap gap-1">
								{#each option.familySlugs as slug (slug)}
									<Badge color="blue">{families.find(family => family.slug === slug)?.name ?? slug}</Badge>
								{/each}
								{#each option.productUUIDs as uuid (uuid)}
									<Badge color="purple">
										{products.find(product => product.uuid === uuid)?.name ?? uuid}
									</Badge>
								{/each}
								{#if option.familySlugs.length + option.productUUIDs.length === 0}
									<span class="text-default text-xs">Nothing</span>
								{/if}
							</div>
						</TableBodyCell>
						<!-- `tabular-nums` opts a money column back out of the table's
						     `overflow-wrap: anywhere`, which would otherwise break a total
						     mid-number in a narrow column and still leave it looking like one. -->
						<TableBodyCell class="dataTable tabular-nums">{fnSavingOf(option)}</TableBodyCell>
						<TableBodyCell class="dataTable">v{option.version}</TableBodyCell>
						<TableBodyCell class="dataTable">{option.selectionCount}</TableBodyCell>
						<TableBodyCell class="dataTable">
							{#if option.isActive}
								<Badge color="green">Offered</Badge>
							{:else}
								<!-- Retired, not deleted: the selections stand, and nobody new sees it. -->
								<Badge color="dark">Retired</Badge>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex justify-end gap-2">
								<Button size="xs" color="alternative" on:click={() => fnStartEdit(option)}>
									<i class="fa-solid fa-pen mr-1" />Edit
								</Button>
								<!--
									Two-step, like every other destructive control: this takes an
									offer off a live product, and the applications reading its key
									for customers who already took it are unaffected — which is the
									part an admin is most likely to get wrong in their head.
								-->
								<ConfirmButton
									size="xs"
									color="red"
									disabled={saving}
									confirmText={option.selectionCount > 0
										? `${option.selectionCount} purchase${option.selectionCount === 1 ? ' took' : 's took'} “${option.title}”, so the record is kept and it is retired instead. Take it off every product and family?`
										: `Delete “${option.title}”? Nobody has taken it, so it goes for good.`}
									on:confirm={() => fnDelete(option)}
								>
									<i class="fa-solid fa-trash mr-1" />Delete
								</ConfirmButton>
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}

	<FormModal
		bind:open={isAdding}
		title="New product option"
		faIcon="sliders"
		submitLabel="Add option"
		size="lg"
		busy={saving}
		canSubmit={canCreate}
		description="Shown as an optional checkbox at checkout. Taking it can discount the purchase and can turn a feature on; leaving it alone changes nothing."
		on:submit={fnCreate}
	>
		<div class="grid md:grid-cols-2 gap-4">
			<!--
				A plain flow list with `spacing="" class="shrink-0"` and no scroller of its own, for
				the reasons `ClickwrapsPanel` documents: the dialog body already scrolls, a nested
				one hides everything past the sixth option behind a hairline scrollbar, and without
				`shrink-0` the box is squeezed narrower than its own square beside a long name.
			-->
			<FormField
				label="Product families it is offered on"
				description="A product family covers every product in it, including ones added later."
			>
				<div class="space-y-1">
					{#each families as family (family.slug)}
						<label class="flex items-center gap-2 text-sm">
							<Checkbox
								checked={draft.familySlugs.includes(family.slug)}
								disabled={saving}
								spacing=""
								class="shrink-0"
								on:change={event =>
									(draft.familySlugs = fnToggle(draft.familySlugs, family.slug, event))}
							/>
							{family.name}
						</label>
					{/each}
				</div>
			</FormField>

			<FormField
				label="Products it is offered on"
				description="For offers that cover one plan rather than a whole product family."
			>
				<div class="space-y-1">
					{#each products as product (product.uuid)}
						<label class="flex items-center gap-2 text-sm">
							<Checkbox
								checked={draft.productUUIDs.includes(product.uuid)}
								disabled={saving}
								spacing=""
								class="shrink-0"
								on:change={event =>
									(draft.productUUIDs = fnToggle(draft.productUUIDs, product.uuid, event))}
							/>
							{product.name}
						</label>
					{/each}
				</div>
			</FormField>

			<FormField id="option-title" label="Label" required description="The words beside the checkbox.">
				<Input id="option-title" bind:value={draft.title} placeholder="Show our logo on your splash screen" disabled={saving} />
			</FormField>

			<FormField
				id="option-key"
				label="Application key"
				required
				description="What the application reads back in the entitlement payload. Lowercase letters, digits, underscores and hyphens. It cannot be changed once anybody has taken the option."
			>
				<Input id="option-key" bind:value={draft.key} placeholder="splash_logo" maxlength={80} disabled={saving} />
			</FormField>

			<!--
				Direction first, then kind. The stored value is signed, and asking for that sign by
				having somebody type a minus is the mistake that goes unnoticed: a discount authored
				as a surcharge moves the price by twice the intended amount the wrong way, and the
				row reads correctly.
			-->
			<FormField
				id="option-effect"
				label="Effect on the price"
				description="An option can change nothing and exist purely to turn a feature on."
			>
				<SelectField id="option-effect" items={EFFECTS} bind:value={draft.effect} disabled={saving} />
			</FormField>

			{#if draft.effect !== 'none'}
				<FormField id="option-kind" label="Measured as">
					<SelectField id="option-kind" items={KINDS} bind:value={draft.kind} disabled={saving} />
				</FormField>
			{/if}

			<FormField id="option-order" label="Order" description="Lower appears first.">
				<Input id="option-order" type="number" min="0" step="1" bind:value={draft.displayOrder} disabled={saving} />
			</FormField>

			{#if draft.effect !== 'none' && draft.kind === 'percent'}
				<FormField
					id="option-percent"
					label={draft.effect === 'charge' ? 'Percentage added' : 'Percentage off'}
					required
					description="A plain number — the direction is set above. Two discounts ticked together add up, capped at 100%."
				>
					<Input id="option-percent" type="number" min="1" max="100" step="1" bind:value={draft.percent} disabled={saving} />
				</FormField>
			{/if}

			{#if draft.effect !== 'none'}
				<FormField
					id="option-cadence"
					label="Charged"
					description="A standing addition to the plan, or a one-off on the first invoice."
				>
					<SelectField id="option-cadence" items={CADENCES} bind:value={draft.cadence} disabled={saving} />
				</FormField>
			{/if}
		</div>

		{#if draft.effect !== 'none' && draft.kind === 'fixed'}
			<!--
				One amount per currency, exactly as a price has it: minor units in two currencies
				are different units, and an option with no amount in the currency a customer is
				billed in is shown to them as unavailable rather than converted at a rate nobody
				chose. `type="text"` with `inputmode="decimal"`, because a number input rejects the
				comma half of Europe types and steps by whole units (recipe 8d).
			-->
			<FormField
				label={draft.effect === 'charge' ? 'Amount added, per currency' : 'Amount off, per currency'}
				description="Plain amounts — the direction is set above. Leave a currency blank to offer nothing in it; the option is then shown as unavailable to customers billed in it."
			>
				<div class="grid sm:grid-cols-2 gap-3">
					{#each currencies as currency (currency.code)}
						<label class="text-sm">
							<span class="block mb-1">{currency.code.toUpperCase()}</span>
							<Input
								type="text"
								inputmode="decimal"
								placeholder={majorUnitsFrom(0, currency.minorUnitDigits)}
								bind:value={draft.amounts[currency.code]}
								disabled={saving}
								aria-label={`Amount off in ${currency.code.toUpperCase()}`}
							/>
						</label>
					{/each}
				</div>
			</FormField>
		{/if}

		<FormField
			id="option-body"
			label="What they are taking on"
			required
			description="HTML. Rendered inside `.richtext`, so paragraphs and lists survive."
		>
			<Textarea id="option-body" rows="6" bind:value={draft.bodyHTML} disabled={saving} />
		</FormField>
	</FormModal>

	{#if editing !== null}
		<FormModal
			open={true}
			title={`Edit ${editing.title}`}
			faIcon="pen"
			submitLabel="Save option"
			size="lg"
			busy={saving}
			canSubmit={canSaveEdit}
			description="Customers who already took it keep the terms they were offered, because the selection recorded what they were quoted."
			on:submit={fnSaveEdit}
			on:cancel={() => (editingUUID = '')}
		>
			<div class="grid md:grid-cols-2 gap-4">
				<FormField
					label="Product families it is offered on"
					description="Adding one offers it to every future buyer in that product family. Purchases already made are unaffected."
				>
					<div class="space-y-1">
						{#each families as family (family.slug)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.familySlugs.includes(family.slug)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.familySlugs = fnToggle(edit.familySlugs, family.slug, event))}
								/>
								{family.name}
							</label>
						{/each}
					</div>
				</FormField>

				<FormField label="Products it is offered on">
					<div class="space-y-1">
						{#each products as product (product.uuid)}
							<label class="flex items-center gap-2 text-sm">
								<Checkbox
									checked={edit.productUUIDs.includes(product.uuid)}
									disabled={saving}
									spacing=""
									class="shrink-0"
									on:change={event =>
										(edit.productUUIDs = fnToggle(edit.productUUIDs, product.uuid, event))}
								/>
								{product.name}
							</label>
						{/each}
					</div>
				</FormField>

				<FormField id="option-edit-title" label="Label" required>
					<Input id="option-edit-title" bind:value={edit.title} disabled={saving} />
				</FormField>

				<FormField
					id="option-edit-key"
					label="Application key"
					required
					description={editing.keyIsEditable
						? 'What the application reads back. It freezes the first time somebody takes this option.'
						: 'Frozen: this key is on every selection and every entitlement payload, so changing it would strand the customers who already have it.'}
				>
					<Input
						id="option-edit-key"
						bind:value={edit.key}
						maxlength={80}
						disabled={saving || !editing.keyIsEditable}
					/>
				</FormField>

				<FormField id="option-edit-effect" label="Effect on the price">
					<SelectField id="option-edit-effect" items={EFFECTS} bind:value={edit.effect} disabled={saving} />
				</FormField>

				{#if edit.effect !== 'none'}
					<FormField id="option-edit-kind" label="Measured as">
						<SelectField id="option-edit-kind" items={KINDS} bind:value={edit.kind} disabled={saving} />
					</FormField>

					<FormField id="option-edit-cadence" label="Charged">
						<SelectField id="option-edit-cadence" items={CADENCES} bind:value={edit.cadence} disabled={saving} />
					</FormField>
				{/if}

				<FormField id="option-edit-order" label="Order">
					<Input id="option-edit-order" type="number" min="0" step="1" bind:value={edit.displayOrder} disabled={saving} />
				</FormField>

				{#if edit.effect !== 'none' && edit.kind === 'percent'}
					<FormField
						id="option-edit-percent"
						label={edit.effect === 'charge' ? 'Percentage added' : 'Percentage off'}
						required
					>
						<Input id="option-edit-percent" type="number" min="1" max="100" step="1" bind:value={edit.percent} disabled={saving} />
					</FormField>
				{/if}
			</div>

			{#if edit.effect !== 'none' && edit.kind === 'fixed'}
				<FormField
					label={edit.effect === 'charge' ? 'Amount added, per currency' : 'Amount off, per currency'}
					description="Plain amounts — the direction is set above. Leave a currency blank to offer nothing in it."
				>
					<div class="grid sm:grid-cols-2 gap-3">
						{#each currencies as currency (currency.code)}
							<label class="text-sm">
								<span class="block mb-1">{currency.code.toUpperCase()}</span>
								<Input
									type="text"
									inputmode="decimal"
									placeholder={majorUnitsFrom(0, currency.minorUnitDigits)}
									bind:value={edit.amounts[currency.code]}
									disabled={saving}
									aria-label={`Amount off in ${currency.code.toUpperCase()}`}
								/>
							</label>
						{/each}
					</div>
				</FormField>
			{/if}

			<FormField
				id="option-edit-body"
				label="What they are taking on"
				required
				description="HTML. Rendered inside `.richtext`."
			>
				<Textarea id="option-edit-body" rows="6" bind:value={edit.bodyHTML} disabled={saving} />
			</FormField>

			{#if versionBumps}
				<!--
					Said before Save rather than after, as the terms panel says it — and the
					discount half matters more, because it is money: a buyer with the page already
					open was quoted a figure, and without the bump they would be charged against a
					different one with nothing on either screen saying so.
				-->
				<p class="text-sm text-amber-700 dark:text-amber-400">
					<i class="fa-solid fa-triangle-exclamation mr-2" />
					{wordingChanged && priceChanged
						? 'The wording and the price changed'
						: wordingChanged
							? 'The wording changed'
							: 'The price changed'}, so it becomes
					<strong>v{editing.version + 1}</strong>. Buyers part-way through checkout will be
					asked to look again; the {editing.selectionCount} purchase{editing.selectionCount === 1 ? '' : 's'}
					that already took it keep the terms they were quoted.
				</p>
			{/if}

			<div class="flex items-center gap-3">
				<Toggle id="option-edit-active" bind:checked={edit.isActive} disabled={saving} />
				<label for="option-edit-active" class="text-sm">
					Offered at checkout. Turning it off stops offering it to anyone new. It does not
					withdraw it from the {editing.selectionCount} purchase{editing.selectionCount === 1 ? '' : 's'}
					that already took it, and the applications reading its key go on reading it.
				</label>
			</div>
		</FormModal>
	{/if}
</Panel>
